import { IImageLibraryDto } from "@kuika/kuika-cl-model";
import { ChangeEvent, Component, MouseEvent, ReactNode } from "react";
import { ImageLibraryService } from "../../services/image-library-service";
import { LoadingPresentationBase, resolveLoadingPresentation } from "../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../shared/utility/main-functions";
import SearchSvg from "../../svgs/search.svg";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import KuikaFileInput from "../ui-kit/kuika-file-input/kuika-file-input";
import KuikaInput from "../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../ui-kit/kuika-popover/kuika-popover";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSpinnerSmall from "../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaTooltip from "../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaText from "../ui-kit/typography/Text/Text";
import "./file-picker.scss";
import { AIImageGenerator } from "./ai/ai-image-generator";
import { ResourceImage } from "./resource-image";

enum EImageUploadType {
  WithDragAndDrop = 0,
  WithAI = 1
}
export interface IFilePickerState {
  searchText: string;
  isDataLoading: boolean;
  selectedImageUploadType?: EImageUploadType;
  /** File name of the image whose delete confirmation is open, or null when none is. */
  pendingDeleteFileName: string | null;
}

export interface IFilePickerProps {
  isLoading?: boolean;
  isDeleteEnabled?: boolean;
  files: IImageLibraryDto[];
  acceptedFileTypes?: string;
  showFileDragger?: boolean;
  enableGenerateWithAI?: boolean;
  fileSize?: number;
  enableDeselect?: boolean;
  getImageLibrary: () => void;
  onChange?: (value: string, image: IImageLibraryDto) => void;
  onDeselect?: () => void;
  selectedId: string | undefined;
  disabled?: boolean;
}

export class FilePicker extends Component<IFilePickerProps, IFilePickerState> {
  constructor(props) {
    super(props);
    this.state = {
      searchText: "",
      isDataLoading: false,
      pendingDeleteFileName: null
    };
  }

  handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchText: e.target.value });
  };

  dragAndDropContainer(): ReactNode {
    return (
      <KuikaFileInput
        variant="dropzone"
        multiple
        dataTestId="chooseFromComputerInput"
        className="kuika__file-picker__dropzone"
        accept={this.props.acceptedFileTypes}
        disabled={this.props.disabled}
        onFiles={this.handleFiles}
        onReject={this.handleRejectedFiles}
      >
        <KuikaText className="kuika__file-picker__dropzone-text">{`Drag & Drop file or `}</KuikaText>
        <KuikaText className="kuika__file-picker__dropzone-text kuika__file-picker__dropzone-text--link">
          Choose from computer
        </KuikaText>

        {this.props.acceptedFileTypes && (
          <KuikaSmallMediumText className="kuika__file-picker__dropzone-hint">
            Formats: {this.getAcceptedFileTypeText()}
          </KuikaSmallMediumText>
        )}
        {this.props.fileSize && (
          <KuikaSmallMediumText className="kuika__file-picker__dropzone-hint">
            Size: {this.props.fileSize.toFixed(2)}MB
          </KuikaSmallMediumText>
        )}
      </KuikaFileInput>
    );
  }

  readJsonFile = (file: File, callback: (result: string) => void): void => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result as string));
    reader.readAsDataURL(file);
  };

  // The dropzone hands over every file of a pick or a drop at once, where antd's
  // Upload fired its onChange once per file. Both ends of the picker are still
  // single-file, so keep the per-file loop the old callback shape gave us for free.
  handleFiles = (files: File[]): void => {
    for (const file of files) {
      this.handleFile(file);
    }
  };

  handleFile = (file: File): void => {
    this.readJsonFile(file, (jsonStr: string): void => {
      if (!KMainFunctions.checkBase64FileSize(jsonStr, this.props.fileSize)) {
        KMainFunctions.displayErrorNotification(`File size is higher than ${this.props.fileSize?.toFixed(2)}MB`);
        return;
      }

      const data = {} as IImageLibraryDto;
      data.fileName = file.name;
      data.content = jsonStr;
      const allData = [] as IImageLibraryDto[];
      allData.push(data);
      this.addImage(allData);
    });
  };

  // A file `accept` turned away never reaches `handleFile` — the kit's dropzone filters a pick
  // and a drop alike and hands only the accepted files to `onFiles`.
  handleRejectedFiles = (): void => {
    KMainFunctions.displayErrorNotification("Please select a valid file.");
  };

  addImage = async (
    data,
    cb?: {
      onPrev?: () => void;
      onSuccess?: () => void;
      onError?: () => void;
      onFinish?: () => void;
    }
  ) => {
    try {
      if (cb?.onPrev) cb.onPrev();

      this.setState({ isDataLoading: true });
      const res = await ImageLibraryService.AddOrUpdateResource(data);
      if (res && res.status === 200) {
        if (cb?.onSuccess) cb.onSuccess();

        this.props.getImageLibrary();
      }
    } catch (error) {
      if (cb?.onError) cb.onError();

      KMainFunctions.exceptionHandler(error);
    } finally {
      if (cb?.onFinish) cb.onFinish();

      this.setState({ isDataLoading: false });
    }
  };

  deleteImage = async (e: MouseEvent, fileName: string) => {
    e.stopPropagation();

    this.setState({ isDataLoading: true, pendingDeleteFileName: null });
    try {
      const res = await ImageLibraryService.Delete(fileName);
      if (res && res.status === 200) {
        this.props.getImageLibrary();
      }
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isDataLoading: false });
    }
  };

  sendContent = (img: IImageLibraryDto, image: IImageLibraryDto) => {
    this.props.onChange?.(img.content, image);
  };

  getAcceptedFileTypeText(): string | undefined {
    if (!this.props.acceptedFileTypes) return;

    const types = this.props.acceptedFileTypes.toUpperCase().split(".").join("").split(",");

    if (types.length > 1) {
      types[types.length - 2] = `${types[types.length - 2]} and ${types[types.length - 1]}`;
      types.splice(-1, 1);
    }

    return types.toString();
  }

  render() {
    // Only the parent's fetch is a load of what the list shows. `isDataLoading` is this picker's own
    // upload or delete — an action — which keeps its lock on the list below (as in the image picker);
    // the refetch an upload ends with runs inside that lock, so it never trades the lock for an outline.
    const filesPresentation: LoadingPresentationBase = resolveLoadingPresentation(
      this.props.isLoading === true && !this.state.isDataLoading,
      (this.props.files?.length ?? 0) > 0
    );

    return (
      <div className="kuika__file-picker__container">
        {/* Framed, like every other field in the panel: borderless left it as a magnifier and a word
            floating on the card, with nothing to say it could be typed into. */}
        <KuikaInput
          className="kuika__file-picker__search-input"
          placeholder="Search..."
          leftIcon={<SearchSvg />}
          onChange={this.handleSearchChange}
          value={this.state.searchText}
        />
        {this.props.enableGenerateWithAI && (
          <AIImageGenerator
            isUploaderBusy={this.state.isDataLoading}
            uploadImage={this.addImage}
            onClose={() => {
              this.setState({ selectedImageUploadType: undefined });
            }}
            isActive={this.state.selectedImageUploadType === EImageUploadType.WithAI}
            placement="leftTop"
          >
            <div>
              <div className="kuika__file-picker__upload-type-container">
                {/* `sm`, not the default `md`: the pair sits in a 225px property section and at md
                    they filled it to the pixel — 91 + 134 with no gap left between them, so the
                    labels read as oversized for the panel they are in. */}
                <KuikaButton
                  data-testid="valuePropertyAddImageButton"
                  size="sm"
                  type={
                    this.state.selectedImageUploadType === EImageUploadType.WithDragAndDrop ? "primary" : "secondary"
                  }
                  onClick={() =>
                    this.setState({
                      selectedImageUploadType: EImageUploadType.WithDragAndDrop
                    })
                  }
                >
                  ADD IMAGE
                </KuikaButton>

                <KuikaButton
                  data-testid="valuePropertyGenerateWithAiButton"
                  size="sm"
                  type={this.state.selectedImageUploadType === EImageUploadType.WithAI ? "primary" : "secondary"}
                  onClick={() =>
                    this.setState({
                      selectedImageUploadType: EImageUploadType.WithAI
                    })
                  }
                >
                  GENERATE WITH AI
                </KuikaButton>
              </div>
            </div>
          </AIImageGenerator>
        )}
        {((this.props.enableGenerateWithAI &&
          this.state.selectedImageUploadType === EImageUploadType.WithDragAndDrop) ||
          this.props.showFileDragger) &&
          this.dragAndDropContainer()}
        <div>
          {filesPresentation === "skeleton" ? (
            <KuikaSkeletonRegion label="Loading files">
              <KuikaSkeletonList rows={6} />
            </KuikaSkeletonRegion>
          ) : (
            <KuikaSpinnerSmall spinning={this.state.isDataLoading} size="small">
              <div className={"kuika_image_library_multiple_list"}>
                {this.props.enableDeselect && (
                  <div
                    className={"kuika_image_library_background_img_card_wrapper"}
                    onClick={() => {
                      if (this.props.onDeselect) this.props.onDeselect();
                    }}
                  >
                    <div className={"kuika_image_library_background_img_list_card"}>
                      <div className="kuika_image_library_multiple_photo kuika__file-picker__deselect">
                        <div>
                          <svg height="46px" viewBox="0 0 384 384" width="46px" xmlns="http://www.w3.org/2000/svg">
                            <path d="m192 384c105.863281 0 192-86.128906 192-192 0-51.328125-19.960938-99.550781-56.207031-135.792969-36.25-36.246093-84.464844-56.207031-135.792969-56.207031-105.871094 0-192 86.128906-192 192 0 51.328125 19.960938 99.550781 56.207031 135.792969 36.242188 36.246093 84.464844 56.207031 135.792969 56.207031zm0-32c-37.390625 0-72.800781-12.710938-101.296875-36.078125l225.21875-225.21875c23.367187 28.496094 36.078125 63.90625 36.078125 101.296875 0 88.222656-71.777344 160-160 160zm0-320c37.382812 0 72.800781 12.710938 101.289062 36.078125l-225.210937 225.21875c-23.367187-28.496094-36.078125-63.90625-36.078125-101.296875 0-88.222656 71.777344-160 160-160zm0 0" />
                          </svg>
                        </div>
                        <div className="kuika__file-picker__deselect-label">None</div>
                      </div>
                    </div>
                  </div>
                )}
                {this.props.files
                  ?.filter(
                    (x) =>
                      x.fileName.toLowerCase().indexOf(this.state.searchText.toLowerCase()) !== -1 &&
                      this.props.acceptedFileTypes?.includes(`.${x.fileName.split(".").pop()}`)
                  )
                  ?.sort((a, b) => a.fileName.localeCompare(b.fileName))
                  ?.map((img) => {
                    return (
                      <div
                        key={img.id}
                        data-testid="filePickerFile"
                        className={
                          img.id === this.props.selectedId
                            ? "kuika_image-picker__selected kuika_image_library_multiple_list_card_wrapper"
                            : "kuika_image_library_multiple_list_card_wrapper"
                        }
                        onClick={() => this.sendContent(img, img)}
                      >
                        <div className={"kuika_image_library_multiple_list_card"}>
                          {this.props.isDeleteEnabled && (
                            <KuikaPopover
                              position="top"
                              align="start"
                              hideCloseIcon
                              className="kuika__file-picker__delete-confirm"
                              visible={this.state.pendingDeleteFileName === img.fileName}
                              onVisibleChange={(visible: boolean): void =>
                                this.setState({ pendingDeleteFileName: visible ? img.fileName : null })
                              }
                              content={
                                <div
                                  className="kuika__file-picker__delete-confirm-body"
                                  onClick={(e): void => e.stopPropagation()}
                                >
                                  <KuikaText className="kuika__file-picker__delete-confirm-text">
                                    Are you sure to delete this photo?
                                  </KuikaText>
                                  <div className="kuika__file-picker__delete-confirm-actions">
                                    <KuikaButton
                                      type="tertiary"
                                      color="neutral"
                                      size="sm"
                                      onClick={(e): void => {
                                        e.stopPropagation();
                                        this.setState({ pendingDeleteFileName: null });
                                      }}
                                    >
                                      No
                                    </KuikaButton>
                                    <KuikaButton
                                      type="primary"
                                      color="danger"
                                      size="sm"
                                      data-testid="confirmDeleteImage"
                                      onClick={(e): void => {
                                        this.deleteImage(e, img.fileName);
                                      }}
                                    >
                                      Yes
                                    </KuikaButton>
                                  </div>
                                </div>
                              }
                            >
                              <div
                                className={"kuika_image_library_multiple_delete"}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                  <g fill="#246eff" fillRule="evenodd">
                                    <g>
                                      <path d="M0 0H24V24H0z" />
                                      <g fill="#fff" fillRule="nonzero">
                                        <path
                                          d="M8.075 0c-1.4 0-2.55 1.15-2.55 2.55H2.867c-.038-.006-.076-.01-.114-.01-.031.002-.062.005-.093.01H1.062c-.23-.003-.443.118-.56.316-.115.199-.115.444 0 .643.117.198.33.32.56.316h1.063v10.837C2.125 15.946 3.179 17 4.463 17h7.224c1.284 0 2.338-1.054 2.338-2.338V3.825h1.063c.23.003.443-.118.56-.316.115-.199.115-.444 0-.643-.117-.198-.33-.32-.56-.316h-1.596c-.068-.011-.138-.011-.207 0h-2.66c0-1.4-1.15-2.55-2.55-2.55zm0 1.275c.712 0 1.275.563 1.275 1.275H6.8c0-.712.563-1.275 1.275-1.275zM3.4 3.825h9.35v10.837c0 .595-.468 1.063-1.063 1.063H4.463c-.595 0-1.063-.468-1.063-1.063V3.825zm3.178 2.116c-.352.005-.633.295-.628.647v6.375c-.003.23.118.443.316.56.199.115.444.115.643 0 .198-.117.32-.33.316-.56V6.588c.002-.173-.065-.339-.187-.46-.122-.123-.288-.19-.46-.187zm2.975 0c-.352.005-.633.295-.628.647v6.375c-.003.23.118.443.316.56.199.115.444.115.643 0 .198-.117.32-.33.316-.56V6.588c.002-.173-.065-.339-.187-.46-.122-.123-.288-.19-.46-.187z"
                                          transform="translate(4 3.5)"
                                        />
                                      </g>
                                    </g>
                                  </g>
                                </svg>
                              </div>
                            </KuikaPopover>
                          )}
                          <div data-testid="propertyGeneratedImage" className={"kuika_image_library_multiple_photo"}>
                            <ResourceImage alt={`_${img.fileName}`} src={img.content} />
                          </div>
                          <div className={"kuika_image_library_multiple_photo_name"}>
                            <KuikaTooltip content={img.fileName}>
                              <span>{img.fileName}</span>
                            </KuikaTooltip>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </KuikaSpinnerSmall>
          )}
        </div>
      </div>
    );
  }
}
